import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Stack,
  Link,
  Divider,
  InputAdornment,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  LockOutlined,
  EmailOutlined,
  PinOutlined,
} from "@mui/icons-material";
import { useNavigate, Link as RouterLink, useSearchParams, useLocation } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import { useRegistration, LoginResult } from "../context/RegistrationContext";
import ApiService from "../services/api";
import { BASELINE_SOFTWARE_VERSION } from "../constants/version";
import BrandLogoMark from "./BrandLogoMark";
import { AccountPathway } from "../constants/accountTypes";
import {
  resolveAuthenticatedHomePath,
  resolveEnterprisePostLoginDestination,
  resolveMsmePostLoginDestination,
} from "../utils/resolvePostLoginDestination";
import { getCurrentUserRole } from '../utils/authRole';
import {
  billingPathForPlanIntent,
  clearPendingBillingPlanId,
  getPendingBillingPlanId,
  setPendingBillingPlanId,
} from "../utils/billingPlanIntent";
import { resolveOtpChannels, type PendingLoginOtpState } from "../utils/otpChannels";

const loginSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Invalid email format")
    .required("Email is required"),
  password: yup.string().required("Password is required"),
});

type LoginFormData = {
  email: string;
  password: string;
};

type LoginStep = "credentials" | "login_otp" | "mfa" | "mfa_enrollment";

const PORTAL_ROLES = [
  { label: "MSME", description: "Carbon workspace" },
  { label: "Admin", description: "Platform administration" },
  { label: "Support", description: "Customer support portal" },
  { label: "Partner", description: "API integrations" },
] as const;

const UserLogin: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, completeLoginFromResponse, isRegistered, setAccountPathway } = useRegistration();
  const selectedPathway: AccountPathway = "msme";
  const selectedPlanId = searchParams.get("plan")?.trim() || null;
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginInfo, setLoginInfo] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [baselineVersion, setBaselineVersion] = useState<string>(BASELINE_SOFTWARE_VERSION);
  const [loginStep, setLoginStep] = useState<LoginStep>("credentials");
  const [loginOtpSessionId, setLoginOtpSessionId] = useState<string | null>(null);
  const [otpChannels, setOtpChannels] = useState({ email: false, mobile: true });
  const [emailOtp, setEmailOtp] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);
  const [enrollmentSecret, setEnrollmentSecret] = useState<string | null>(null);
  const [enrollmentOtpauthUrl, setEnrollmentOtpauthUrl] = useState<string | null>(null);
  // Prevent the "already logged in" effect from overriding onboarding / billing redirects
  // right after a successful login sets isRegistered.
  const skipCredentialsAutoRedirectRef = useRef(false);
  const pendingLoginOtpConsumedRef = useRef(false);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onBlur",
  });

  useEffect(() => {
    setAccountPathway("msme");
  }, [setAccountPathway]);

  useEffect(() => {
    if (selectedPlanId) {
      setPendingBillingPlanId(selectedPlanId);
    }
  }, [selectedPlanId]);

  // Resume login OTP challenge when registration (or another flow) already issued a session.
  useEffect(() => {
    if (pendingLoginOtpConsumedRef.current) {
      return;
    }
    const pending = (location.state as { pendingLoginOtp?: PendingLoginOtpState } | null)?.pendingLoginOtp;
    if (!pending?.sessionId) {
      return;
    }
    pendingLoginOtpConsumedRef.current = true;
    const channels = resolveOtpChannels(pending.channels);
    setLoginStep("login_otp");
    setLoginOtpSessionId(pending.sessionId);
    setOtpChannels(channels);
    setEmailOtp("");
    setMobileOtp("");
    if (pending.email) {
      setValue("email", pending.email);
    }
    setLoginInfo(
      pending.message
        || (channels.mobile && !channels.email
          ? "Enter the OTP sent to your mobile to finish signing in."
          : "Enter the OTP sent to your email/mobile to finish signing in."),
    );
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate, setValue]);

  useEffect(() => {
    if (!isRegistered || loginStep !== "credentials") {
      return;
    }
    // BUG-001: never auto-leave while an OTP challenge session is still open.
    if (loginOtpSessionId) {
      return;
    }
    if (skipCredentialsAutoRedirectRef.current) {
      return;
    }
    navigate(resolveAuthenticatedHomePath());
  }, [isRegistered, navigate, loginStep, loginOtpSessionId]);

  useEffect(() => {
    let isMounted = true;

    const loadBaselineVersion = async () => {
      try {
        const versionMetadata = await ApiService.getVersionMetadata();
        if (isMounted) {
          setBaselineVersion(
            versionMetadata.baselineCodebaseVersion || BASELINE_SOFTWARE_VERSION,
          );
        }
      } catch {
        if (isMounted) {
          setBaselineVersion(BASELINE_SOFTWARE_VERSION);
        }
      }
    };

    loadBaselineVersion();
    return () => {
      isMounted = false;
    };
  }, []);

  const finishAuthenticatedNavigation = async (redirectPath: string | null) => {
    if (redirectPath) {
      navigate(redirectPath);
      return;
    }

    const msmeDestination = getCurrentUserRole() === 'enterprise'
      ? await resolveEnterprisePostLoginDestination()
      : await resolveMsmePostLoginDestination();
    const pendingPlanId = getPendingBillingPlanId();
    if (pendingPlanId && msmeDestination === "/dashboard") {
      clearPendingBillingPlanId();
      navigate(billingPathForPlanIntent(pendingPlanId));
      return;
    }
    navigate(msmeDestination);
  };

  const applyLoginResult = async (result: LoginResult) => {
    if (result.status === "authenticated") {
      // Drop any in-flight OTP challenge before finishing navigation.
      setLoginOtpSessionId(null);
      skipCredentialsAutoRedirectRef.current = true;
      setLoginStep("credentials");
      await finishAuthenticatedNavigation(result.redirectPath);
      return;
    }

    if (result.status === "login_otp_required") {
      const channels = resolveOtpChannels(result.channels);
      // BUG-001: stay on OTP until verify succeeds; ignore stale registered sessions.
      skipCredentialsAutoRedirectRef.current = true;
      setLoginStep("login_otp");
      setLoginOtpSessionId(result.sessionId);
      setOtpChannels(channels);
      setEmailOtp("");
      setMobileOtp("");
      setLoginInfo(
        result.message
          || (channels.mobile && !channels.email
            ? "Enter the OTP sent to your mobile to finish signing in."
            : "Enter the OTP sent to your email/mobile to finish signing in."),
      );
      return;
    }

    if (result.status === "mfa_required") {
      setLoginStep("mfa");
      setMfaToken(result.mfaToken);
      setMfaCode("");
      setLoginInfo(result.message || "Enter the code from your authenticator app.");
      return;
    }

    if (result.status === "mfa_enrollment_required") {
      setLoginStep("mfa_enrollment");
      setEnrollmentToken(result.enrollmentToken);
      setMfaCode("");
      setLoginInfo(
        result.message
          || "Authenticator MFA enrollment is required for this account.",
      );
      try {
        const setup = await ApiService.setupMfaEnrollment({
          enrollmentToken: result.enrollmentToken,
        });
        if (setup?.success) {
          setEnrollmentSecret(setup?.data?.secret || null);
          setEnrollmentOtpauthUrl(setup?.data?.otpauthUrl || null);
        }
      } catch {
        // User can still retry setup from the enrollment step.
      }
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setLoginError(null);
    setLoginInfo(null);
    setIsLoggingIn(true);
    // BUG-001: clear any leftover challenge before a fresh password attempt.
    setLoginOtpSessionId(null);
    setLoginStep("credentials");
    skipCredentialsAutoRedirectRef.current = true;

    try {
      setAccountPathway(selectedPathway);
      // Clear server cookie session too so a leftover HttpOnly cookie cannot
      // finish login while the OTP gate is showing.
      try {
        await ApiService.logout();
      } catch {
        // local clear in login() still runs
      }
      const result = await login(data.email.trim(), data.password, selectedPathway);
      await applyLoginResult(result);
    } catch (error) {
      setLoginError(
        error instanceof Error
          ? error.message
          : "Login failed. Please check your credentials and try again.",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleVerifyLoginOtp = async () => {
    if (!loginOtpSessionId) {
      setLoginError("OTP session expired. Please sign in again.");
      setLoginStep("credentials");
      return;
    }
    if (otpChannels.email && !emailOtp.trim()) {
      setLoginError("Please enter the email OTP.");
      return;
    }
    if (otpChannels.mobile && !mobileOtp.trim()) {
      setLoginError("Please enter the mobile OTP.");
      return;
    }

    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const response = await ApiService.verifyLoginOtp({
        sessionId: loginOtpSessionId,
        ...(otpChannels.email ? { emailOtp: emailOtp.trim() } : {}),
        ...(otpChannels.mobile ? { mobileOtp: mobileOtp.trim() } : {}),
      });
      const result = await completeLoginFromResponse(response);
      await applyLoginResult(result);
    } catch (error) {
      setLoginError(
        error instanceof Error ? error.message : "OTP verification failed.",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleResendLoginOtp = async () => {
    const { email, password } = getValues();
    if (!email || !password) {
      setLoginError("Re-enter email and password to resend OTP.");
      setLoginStep("credentials");
      return;
    }
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const response = await ApiService.resendLoginOtp({ email, password });
      if (!response?.success) {
        throw new Error(response?.message || "Failed to resend OTP");
      }
      setLoginOtpSessionId(response?.data?.sessionId || loginOtpSessionId);
      setOtpChannels(resolveOtpChannels(response?.data?.channels));
      setLoginInfo("A new OTP was sent. Enter the latest code.");
    } catch (error) {
      setLoginError(
        error instanceof Error ? error.message : "Unable to resend OTP.",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaToken || !mfaCode.trim()) {
      setLoginError("Enter your authenticator or backup code.");
      return;
    }
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const response = await ApiService.verifyMfaLogin({
        mfaToken,
        code: mfaCode.trim(),
      });
      const result = await completeLoginFromResponse(response);
      await applyLoginResult(result);
    } catch (error) {
      setLoginError(
        error instanceof Error ? error.message : "MFA verification failed.",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleEnableMfaEnrollment = async () => {
    if (!enrollmentToken || !mfaCode.trim()) {
      setLoginError("Enter the authenticator code to finish MFA enrollment.");
      return;
    }
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const response = await ApiService.enableMfaEnrollment({
        enrollmentToken,
        code: mfaCode.trim(),
      });
      const result = await completeLoginFromResponse(response);
      await applyLoginResult(result);
    } catch (error) {
      setLoginError(
        error instanceof Error ? error.message : "Unable to enable MFA.",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        px: 2,
        overflowY: "auto",
        boxSizing: "border-box",
        ...(isDark
          ? {
              backgroundColor: theme.palette.background.default,
              backgroundImage: `radial-gradient(ellipse 120% 80% at 0% -20%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 55%), radial-gradient(ellipse 100% 60% at 100% 0%, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 50%)`,
            }
          : {
              background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
            }),
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 520,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          background:
            theme.palette.mode === "light"
              ? "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"
              : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.common.white, 0.04)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.12)}`,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <BrandLogoMark width={260} sx={{ mb: 2 }} />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Carbon Intelligence Portal
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, minWidth: 0, wordBreak: "break-word" }}>
            {loginStep === "login_otp"
              ? (otpChannels.mobile && !otpChannels.email
                ? "Verify the OTP sent to your mobile to continue."
                : "Verify the OTP sent to your email/mobile to continue.")
              : loginStep === "mfa" || loginStep === "mfa_enrollment"
                ? "Complete authenticator verification to continue."
                : "One sign-in for all account types — we route you to the right workspace automatically."}
          </Typography>
          {loginStep === "credentials" && (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
            >
              {PORTAL_ROLES.map((role) => (
                <Chip
                  key={role.label}
                  label={role.label}
                  size="small"
                  variant="outlined"
                  title={role.description}
                  sx={{ borderColor: alpha(theme.palette.primary.main, 0.35) }}
                />
              ))}
            </Stack>
          )}
        </Box>

        {loginError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loginError}
          </Alert>
        )}
        {loginInfo && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {loginInfo}
          </Alert>
        )}

        {loginStep === "credentials" && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email Address"
                    type="email"
                    autoComplete="email"
                    error={!!errors.email}
                    helperText={errors.email?.message || "This email is your login credential."}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Box sx={{ textAlign: "right" }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoggingIn}
                startIcon={isLoggingIn ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{
                  py: 1.5,
                  background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #45a049 0%, #1b5e20 100%)",
                  },
                }}
              >
                {isLoggingIn ? "Signing in..." : "Sign In"}
              </Button>
            </Stack>
          </form>
        )}

        {loginStep === "login_otp" && (
          <Stack spacing={3}>
            {otpChannels.email && (
              <TextField
                fullWidth
                label="Email OTP"
                placeholder="Enter 6-digit email OTP"
                value={emailOtp}
                onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "one-time-code",
                  "aria-label": "Email OTP",
                  placeholder: "Enter 6-digit email OTP",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PinOutlined color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {otpChannels.mobile && (
              <TextField
                fullWidth
                label="Mobile OTP"
                placeholder="Enter 6-digit mobile OTP"
                value={mobileOtp}
                onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "one-time-code",
                  "aria-label": "Mobile OTP",
                  placeholder: "Enter 6-digit mobile OTP",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PinOutlined color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <Button
              variant="contained"
              size="large"
              disabled={isLoggingIn}
              onClick={handleVerifyLoginOtp}
              startIcon={isLoggingIn ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
            >
              {isLoggingIn ? "Verifying OTP..." : "Verify OTP & Continue"}
            </Button>
            <Button
              variant="text"
              disabled={isLoggingIn}
              onClick={handleResendLoginOtp}
            >
              Resend OTP
            </Button>
            <Button
              variant="text"
              disabled={isLoggingIn}
              onClick={() => {
                setLoginStep("credentials");
                setLoginInfo(null);
                setLoginError(null);
              }}
            >
              Back to sign in
            </Button>
          </Stack>
        )}

        {(loginStep === "mfa" || loginStep === "mfa_enrollment") && (
          <Stack spacing={3}>
            {loginStep === "mfa_enrollment" && (
              <Alert severity="warning">
                Scan this secret in your authenticator app, then enter the 6-digit code.
                {enrollmentSecret ? (
                  <>
                    <br />
                    <strong>Secret:</strong> {enrollmentSecret}
                  </>
                ) : null}
                {enrollmentOtpauthUrl ? (
                  <>
                    <br />
                    <Typography variant="caption" component="span" sx={{ wordBreak: "break-all" }}>
                      {enrollmentOtpauthUrl}
                    </Typography>
                  </>
                ) : null}
              </Alert>
            )}
            <TextField
              fullWidth
              label={loginStep === "mfa" ? "Authenticator / backup code" : "Authenticator code"}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.trim())}
              inputProps={{ autoComplete: "one-time-code" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PinOutlined color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="large"
              disabled={isLoggingIn}
              onClick={loginStep === "mfa" ? handleVerifyMfa : handleEnableMfaEnrollment}
              startIcon={isLoggingIn ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
            >
              {isLoggingIn
                ? "Verifying..."
                : loginStep === "mfa"
                  ? "Verify MFA & Continue"
                  : "Enable MFA & Continue"}
            </Button>
            <Button
              variant="text"
              disabled={isLoggingIn}
              onClick={() => {
                setLoginStep("credentials");
                setLoginInfo(null);
                setLoginError(null);
              }}
            >
              Back to sign in
            </Button>
          </Stack>
        )}

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Don&apos;t have an account?{" "}
            <Link
              component={RouterLink}
              to="/register"
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 600,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Create Account
            </Link>
          </Typography>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 3 }}
        >
          Platform baseline {baselineVersion}
        </Typography>
      </Paper>
    </Box>
  );
};

export default UserLogin;
